import { logger } from "@platform/browser-logger";
import { BenefitPlanTypeEnum, CoverageTypeEnum } from "@platform/openapi-types";
import { useCallback, useEffect, useRef } from "react";
import {
  AddBenefitPlanGroupArgs,
  DeletePlanDialogData,
  DuplicateBenefitPlanGroupArgs,
} from "@platform/plan-grid-core";
import {
  clearDirtyFlagsForPlans,
  collectPlanIdsForGroup,
  removePlanFromCoverage,
} from "@platform/plan-grid-core";

import {
  RfpCoverage,
  useCreateBenefitPlanGroup,
  useDeleteBenefitPlan,
  useDeleteBenefitPlanGroup,
  useDuplicateBenefitPlan,
  useDuplicateBenefitPlanGroup,
  useListBenefitPlanGroups,
  useUpdateBenefitPlanGroup,
} from "../api";

/**
 * Corrects the productType based on the coverage type.
 * - Dental: uses the requested productType (PPO/HMO)
 * - Vision: always PPO
 * - All others: always notApplicable
 */
export const correctProductType = (
  coverageType: string,
  requestedProductType: BenefitPlanTypeEnum,
): BenefitPlanTypeEnum => {
  if (coverageType === CoverageTypeEnum.vision) {
    return BenefitPlanTypeEnum.ppo;
  }
  if (coverageType !== CoverageTypeEnum.dental) {
    return BenefitPlanTypeEnum.notApplicable;
  }
  return requestedProductType;
};

export const usePlanDialogHandles = (
  rfpId: string,
  benefitPlanGroups: ReturnType<typeof useListBenefitPlanGroups>["data"],
  updateSelectedCoverage: (updatedCoverage: RfpCoverage) => void,
  getCurrentCoverage: () => RfpCoverage | undefined,
  coverage?: RfpCoverage,
): {
  handleAddPlan: (args: AddBenefitPlanGroupArgs) => void;
  handleDuplicatePlan: (args: DuplicateBenefitPlanGroupArgs) => void;
  handleDeletePlan: (planGroupId: string) => void;
  handleDeletePlanLevel: (data: DeletePlanDialogData) => void;
  handleNicknamePlan: (
    planGroupId: string | undefined,
    nickname: string,
    productType?: BenefitPlanTypeEnum,
  ) => void;
  handleAddPlanToGroup: (planGroupId: string, currentSize: number) => void;
  handleDuplicatePlanInGroup: (
    planGroupId: string,
    benefitPlanId: string,
    currentSize: number,
  ) => void;
  isDuplicatingPlan: boolean;
} => {
  const coverageType = coverage?.coverageType;

  const duplicatePlanGroup = useDuplicateBenefitPlanGroup();
  const handleDuplicatePlan = useCallback(
    ({
      nickname,
      insertPosition,
      planGroupId,
      policyPeriod,
      currentPolicyUuid,
    }: DuplicateBenefitPlanGroupArgs) => {
      if (!rfpId || !coverageType || !planGroupId) {
        logger.error(
          "[PlanDesigner] Cannot duplicate plan - missing RFP ID, selected coverage, or plan group ID",
        );
        return;
      }
      logger.info(`[PlanDesigner] Duplicating plan: nickname: ${nickname}`);

      duplicatePlanGroup.mutate({
        rfpId,
        coverageType,
        insertPosition,
        referenceGroupId: planGroupId,
        benefitPlanGroup: {
          ...(nickname && { nickname }),
          ...(currentPolicyUuid && { currentPolicyUuid }),
          policyPeriod,
        },
      });
    },
    [duplicatePlanGroup, rfpId, coverageType],
  );

  const createPlanGroup = useCreateBenefitPlanGroup();
  const handleAddPlan = useCallback(
    ({
      nickname,
      productType,
      size,
      insertPosition,
      position,
      policyPeriod,
      currentPolicyUuid,
    }: AddBenefitPlanGroupArgs) => {
      if (!rfpId || !coverageType) {
        logger.error(
          "[PlanDesigner] Cannot add plan - missing RFP ID or selected coverage",
        );
        return;
      }
      const newPosition = insertPosition === "before" ? position : position + 1;

      createPlanGroup.mutate({
        rfpId,
        coverageType,
        benefitPlanGroup: {
          ...(nickname && { nickname }),
          ...(currentPolicyUuid && { currentPolicyUuid }),
          productType: correctProductType(coverageType, productType),
          size,
          position: newPosition,
          policyPeriod,
        },
      });
    },
    [createPlanGroup, rfpId, coverageType],
  );

  const deletePlanGroup = useDeleteBenefitPlanGroup();
  const deleteBenefitPlan = useDeleteBenefitPlan();
  const handleDeletePlan = useCallback(
    (planGroupId: string) => {
      if (!rfpId || !coverageType) {
        logger.error(
          "[PlanDesigner] Cannot delete plan - missing RFP ID or selected coverage",
        );
        return;
      }

      logger.info(`[PlanDesigner] Deleting plan group ${planGroupId}`);

      // Clear dirty flags for the plans being deleted so that autosave
      // won't PATCH them while the DELETE request is in flight. We keep
      // the plans themselves in benefitPlans so their values remain
      // visible if the backend rejects the delete (e.g. last required-
      // period plan). On success the query invalidation will reconcile
      // local state from the server.
      const planIds = collectPlanIdsForGroup(planGroupId, benefitPlanGroups);
      const currentCoverage = getCurrentCoverage();
      if (currentCoverage) {
        const cleaned = clearDirtyFlagsForPlans(currentCoverage, planIds);
        if (cleaned) {
          updateSelectedCoverage(cleaned);
        }
      }

      deletePlanGroup.mutate({
        id: planGroupId,
        rfpId,
        coverageType,
      });
    },
    [
      deletePlanGroup,
      rfpId,
      coverageType,
      benefitPlanGroups,
      updateSelectedCoverage,
      getCurrentCoverage,
    ],
  );

  const handleDeletePlanLevel = useCallback(
    ({ planGroupId, benefitPlanId }: DeletePlanDialogData) => {
      if (!benefitPlanId) {
        logger.warn(
          "[PlanDesigner] Cannot delete plan level - missing benefit plan ID",
          { planGroupId },
        );
        return;
      }

      logger.info("[PlanDesigner] Deleting plan level", {
        planGroupId,
        benefitPlanId,
      });

      const currentCoverage = getCurrentCoverage();
      if (currentCoverage) {
        const nextCoverage = removePlanFromCoverage(
          currentCoverage,
          benefitPlanId,
        );
        if (nextCoverage) {
          updateSelectedCoverage(nextCoverage);
        }
      }

      deleteBenefitPlan.mutate({
        rfpId,
        benefitPlanId,
      });
    },
    [deleteBenefitPlan, getCurrentCoverage, rfpId, updateSelectedCoverage],
  );

  // Mutation hooks for benefit plan group operations
  const updateBenefitPlanGroupMutation = useUpdateBenefitPlanGroup({
    onSuccess: (response) => {
      logger.info("[PlanEditor] Successfully updated benefit plan group:", {
        response,
      });
    },
    onError: (error) => {
      logger.error("[PlanEditor] Error updating benefit plan group:", error);
    },
  });
  // Depend on the stable `mutate`, not the mutation object — React Query
  // returns a new object every render, and the group handlers feed
  // PlanEditor's columnDefs memo, so an unstable identity here rebuilds the
  // grid header on every render (killing any in-progress column resize).
  const { mutate: updateBenefitPlanGroup } = updateBenefitPlanGroupMutation;
  const handleNicknamePlan = useCallback(
    (
      planGroupId: string | undefined,
      nickname: string,
      productType?: BenefitPlanTypeEnum,
    ) => {
      logger.info(`[PlanEditor] Updating plan group nickname`, {
        planGroupId,
        nickname,
        productType,
      });

      // Only proceed if we have the required data for API call
      if (!planGroupId || !rfpId || !coverageType || !productType) {
        logger.warn(
          "[PlanEditor] Cannot update plan nickname - missing required data",
          {
            planGroupId,
            rfpId,
            coverageType,
            productType,
          },
        );
        return;
      }

      // Call the mutation with structured data from columnContext
      updateBenefitPlanGroup({
        id: planGroupId,
        nickname,
        rfpId,
        coverageType,
        productType,
      });
    },
    [updateBenefitPlanGroup, rfpId, coverageType],
  );

  const handleAddPlanToGroup = useCallback(
    (planGroupId: string, _currentSize: number) => {
      const group = benefitPlanGroups?.find((g) => g.id === planGroupId);
      if (!rfpId || !coverageType || !group) {
        logger.warn(
          "[PlanDesigner] Cannot add plan to group - missing required data",
          { planGroupId, rfpId, coverageType },
        );
        return;
      }

      const latestSize = group.benefitPlans.length;

      logger.info("[PlanDesigner] Adding plan to group", {
        planGroupId,
        currentSize: latestSize,
        newSize: latestSize + 1,
      });

      updateBenefitPlanGroup({
        id: planGroupId,
        size: latestSize + 1,
        rfpId,
        coverageType,
        productType: group.productType,
      });
    },
    [updateBenefitPlanGroup, rfpId, coverageType, benefitPlanGroups],
  );

  const duplicateBenefitPlanMutation = useDuplicateBenefitPlan({
    onError: (error) => {
      logger.error("[PlanEditor] Error duplicating benefit plan:", error);
    },
  });
  // Stable `mutate` for the same columnDefs-identity reason as above.
  const { mutate: duplicateBenefitPlanInGroup } = duplicateBenefitPlanMutation;

  const handleDuplicatePlanInGroup = useCallback(
    (planGroupId: string, benefitPlanId: string, _currentSize: number) => {
      if (!rfpId) {
        logger.warn("[PlanDesigner] Cannot duplicate plan - missing rfpId");

        return;
      }

      logger.info("[PlanDesigner] Duplicating plan in group", {
        planGroupId,
        benefitPlanId,
      });
      duplicateBenefitPlanInGroup({ rfpId, benefitPlanId });
    },
    [duplicateBenefitPlanInGroup, rfpId],
  );

  // Track whether a duplicate mutation was recently in flight so the loading
  // indicator can persist through the subsequent query refetch.
  const wasDuplicatingRef = useRef(false);
  if (duplicateBenefitPlanMutation.isPending) {
    wasDuplicatingRef.current = true;
  }
  const isDuplicatingPlan: boolean =
    duplicateBenefitPlanMutation.isPending || wasDuplicatingRef.current;

  // Reset once the mutation is done and data has settled
  useEffect(() => {
    if (!duplicateBenefitPlanMutation.isPending && wasDuplicatingRef.current) {
      wasDuplicatingRef.current = false;
    }
  }, [duplicateBenefitPlanMutation.isPending, benefitPlanGroups]);

  return {
    handleDuplicatePlan,
    handleAddPlan,
    handleDeletePlan,
    handleDeletePlanLevel,
    handleNicknamePlan,
    handleAddPlanToGroup,
    handleDuplicatePlanInGroup,
    isDuplicatingPlan,
  };
};
